import React, { FunctionComponent, memo } from 'react';
import { Divider, Icons, InputField } from 'plume-ui';
import { OutreachTypes } from '../../../../../../../urls';
import { OutreachConfigFormDto } from '../../../../../../Outreach/hooks/useOutreach';
import {
  OutreachConfigSections,
  SupportOutreachTriggerFormFields,
  SupportTriggerRendererProps,
} from '../../../../../types';
import { FormikSupportOutreachTriggerValue } from '../support/types';
import useFormControls from '../../../hooks/useFormControls';
import FormattedMessage from '../../../../../../../utils/components/FormattedMessage';
import OutreachConfigFieldMessages from '../../../../../../Outreach/components/OutreachConfigFieldMessages';
import { FrequencyUnits } from '../../../helpers';
import OutreachConfigWeekdays from '../../../../../../Outreach/components/OutreachConfigWeekdays';
import MaxFrequencyFields from './MaxFrequencyFields';
import SupportTimeTriggerFooter from './SupportTimeTriggerFooter';

export type SupportTimeTriggerDropdownProperties =
  | SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit
  | SupportOutreachTriggerFormFields.TimeRepeatEveryUnit;

const mapOutreachTypeToSection: Record<
  OutreachTypes,
  keyof OutreachConfigFormDto
> = {
  [OutreachTypes.Push]: OutreachConfigSections.SubscriberOutreachTrigger,
  [OutreachTypes.Webhooks]:
    OutreachConfigSections.SupportOutreachTriggerWebhook,
  [OutreachTypes.Email]: OutreachConfigSections.SupportOutreachTrigger,
};

const SupportTimeTrigger: FunctionComponent<SupportTriggerRendererProps<
  FormikSupportOutreachTriggerValue,
  SupportTimeTriggerDropdownProperties
>> = ({ field, form, renderDropdownFor, outreachType }) => {
  const { setPropertyValue, getPropertyValue } = useFormControls<
    FormikSupportOutreachTriggerValue
  >(field, form);

  const section = mapOutreachTypeToSection[outreachType];
  const shouldShowScheduleSection = !Boolean(
    (form.values as any)[section][
      SupportOutreachTriggerFormFields.IsOneTimeOnly
    ],
  );
  const selectedUnit = getPropertyValue(
    SupportOutreachTriggerFormFields.TimeRepeatEveryUnit,
  );

  const noClearMessageLabelId =
    outreachType === OutreachTypes.Email
      ? 'outreach.form.supportOutreachTriggerSection.noClearEmail'
      : 'outreach.form.subscriberOutreachTriggerSection.noClearEmail';

  return (
    <>
      {shouldShowScheduleSection && (
        <>
          <div className="OutreachContainer__subscriber-outreach-trigger-label">
            <span className="color-required">* </span>
            <FormattedMessage id="outreach.form.supportOutreachTriggerSection.schedule" />
          </div>
          <div className="OutreachContainer__subscriber-outreach-trigger-field OutreachContainer__subscriber-outreach-trigger-field--repeat">
            <div className="OutreachContainer__subscriber-outreach-trigger-field-wrapper">
              <FormattedMessage id="outreach.form.supportOutreachTriggerSection.scheduleRepeat" />
              <InputField
                name={SupportOutreachTriggerFormFields.TimeRepeatEvery}
                type="number"
                step="1"
                min={0}
                value={
                  field.value[SupportOutreachTriggerFormFields.TimeRepeatEvery]
                }
                classes={(current) => ({
                  ...current,
                  root: `${current.root} m-r-s m-l-s`,
                })}
                onInput={(e) =>
                  setPropertyValue(
                    SupportOutreachTriggerFormFields.TimeRepeatEvery,
                    parseFloat(e.currentTarget.value) || 0,
                  )
                }
              />
              {renderDropdownFor(
                SupportOutreachTriggerFormFields.TimeRepeatEveryUnit,
              )}
            </div>
            <OutreachConfigFieldMessages
              propertyName={SupportOutreachTriggerFormFields.TimeRepeatEvery}
              field={field}
              form={form}
            />
          </div>
          {selectedUnit === FrequencyUnits.Week && (
            <OutreachConfigWeekdays
              propertyName={SupportOutreachTriggerFormFields.TimeRepeatWeekdays}
              field={field}
              form={form}
            />
          )}
        </>
      )}
      <MaxFrequencyFields
        renderDropdownFor={renderDropdownFor}
        field={field}
        form={form}
        outreachType={outreachType}
      />
      <p className="OutreachContainer__no-clear-email">
        <Icons.InfoIcon width={16} />
        <FormattedMessage id={noClearMessageLabelId} />
      </p>
      <Divider />
      <SupportTimeTriggerFooter outreachType={outreachType} />
    </>
  );
};

export default memo(SupportTimeTrigger);
